import React, { useState, useEffect } from 'react';
import HomeNav from '../navigations/homenav';
import Footer from '../navigations/Footer';
import {
  DocumentIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassPlusIcon,
  MagnifyingGlassMinusIcon,
  MagnifyingGlassIcon,
  ShareIcon,
  XMarkIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon
} from '@heroicons/react/24/outline';
import { useLanguageContext } from '../navigations/homenav';

// Add page turning animations CSS
const pageTurningStyles = `
  @keyframes page-turn-left {
    0% { transform: translateX(0) rotateY(0deg); }
    50% { transform: translateX(-50%) rotateY(-90deg); }
    100% { transform: translateX(-100%) rotateY(-180deg); }
  }

  @keyframes page-turn-right {
    0% { transform: translateX(0) rotateY(0deg); }
    50% { transform: translateX(50%) rotateY(90deg); }
    100% { transform: translateX(100%) rotateY(180deg); }
  }

  .animate-page-turn-left {
    animation: page-turn-left 0.6s ease-in-out forwards;
    transform-origin: left center;
  }

  .animate-page-turn-right {
    animation: page-turn-right 0.6s ease-in-out forwards;
    transform-origin: right center;
  }
`;

// Inject styles into document head
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = pageTurningStyles;
  document.head.appendChild(styleElement);
}

// PDF Viewer Component (Styled like a video player)
const PDFViewer = ({ pdfUrl, title }) => {
  const { t } = useLanguageContext();
  const [currentPage, setCurrentPage] = useState(1);
  const [isTurning, setIsTurning] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showShare, setShowShare] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [searchResults, setSearchResults] = useState(null);
  const totalPages = 24;

  // Mock PDF content for search simulation
  const pdfContent = {
    1: "AAAPD Annual Report 2024 - Addis Ababa Association of Persons with Disabilities",
    2: "Executive Summary and Key Achievements in Disability Rights and Inclusion Programs",
    3: "Financial Overview and Budget Allocation for Community Support Initiatives",
    4: "Education Programs and Accessibility Improvements Across Addis Ababa Schools",
    5: "Healthcare Services and Medical Support for Persons with Disabilities",
    6: "Employment and Vocational Training Programs for Economic Empowerment",
    7: "Community Engagement and Advocacy Campaigns for Disability Rights",
    8: "Partnerships and Collaborations with Government and NGO Organizations",
    9: "Challenges and Solutions in Implementing Inclusive Policies",
    10: "Success Stories and Testimonials from Program Beneficiaries",
    11: "Future Goals and Strategic Planning for Enhanced Disability Inclusion",
    12: "Monitoring and Evaluation Framework for Program Effectiveness",
    13: "Technology and Innovation in Assistive Devices and Accessibility Tools",
    14: "Gender Mainstreaming in Disability Programs and Women's Empowerment",
    15: "Youth and Children with Disabilities - Special Programs and Support",
    16: "Mental Health and Psychosocial Support Services for Vulnerable Groups",
    17: "Emergency Response and Disaster Preparedness for Persons with Disabilities",
    18: "Sustainable Development Goals Alignment and SDG Implementation Progress",
    19: "Research and Data Collection on Disability Statistics in Ethiopia",
    20: "Capacity Building and Training Programs for Staff and Volunteers",
    21: "Resource Mobilization and Fundraising Strategies for Program Sustainability",
    22: "Policy Advocacy and Legal Framework Development for Disability Rights",
    23: "International Cooperation and Knowledge Sharing with Global Partners",
    24: "Conclusion and Call to Action for Inclusive Society Development"
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setIsTurning(true);
      setTimeout(() => {
        setCurrentPage(currentPage - 1);
        setIsTurning(false);
      }, 300);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setIsTurning(true);
      setTimeout(() => {
        setCurrentPage(currentPage + 1);
        setIsTurning(false);
      }, 300);
    }
  };

  const handleFullscreen = () => {
    if (!document.fullscreenElement) {
      // Store current scroll position before entering fullscreen
      setScrollPosition(window.scrollY);

      // Enter fullscreen
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      // Exit fullscreen and restore scroll position
      document.exitFullscreen();
      setIsFullscreen(false);

      // Restore scroll position after a short delay
      setTimeout(() => {
        window.scrollTo(0, scrollPosition);
      }, 100);
    }
  };

  const handleSearchResultClick = (pageNumber) => {
    setCurrentPage(pageNumber);
    setShowSearch(false);

    // Use browser's built-in find functionality to highlight the search term
    setTimeout(() => {
      if (window.find && searchTerm.trim()) {
        // Clear any previous highlights and search for the new term
        window.getSelection().removeAllRanges();

        // Try multiple search attempts in case the PDF takes time to load
        let attempts = 0;
        const searchInterval = setInterval(() => {
          attempts++;
          if (window.find(searchTerm, false, false, true, false, true, false)) {
            clearInterval(searchInterval);
          } else if (attempts >= 10) {
            clearInterval(searchInterval);
          }
        }, 200);
      }
    }, 500);
  };

  const handleSearch = () => {
    if (!searchTerm.trim()) {
      setSearchResults(null);
      return;
    }

    const term = searchTerm.toLowerCase();
    const results = [];

    // Search through PDF content
    Object.entries(pdfContent).forEach(([page, content]) => {
      const lowerContent = content.toLowerCase();
      if (lowerContent.includes(term)) {
        results.push({
          page: parseInt(page),
          content: content,
          matches: (lowerContent.match(new RegExp(term, 'g')) || []).length
        });
      }
    });

    setSearchResults({
      term: searchTerm,
      results: results,
      count: results.length
    });
  };

  const handleShare = (platform) => {
    const url = window.location.href;
    const shareTitle = title || t('annualReportTitlePrefix');

    const shareUrls = {
      telegram: `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(shareTitle)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(shareTitle)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(`${shareTitle} ${url}`)}`,
      email: `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(`Check out this report: ${url}`)}`
    };

    window.open(shareUrls[platform], '_blank');
    setShowShare(false);
  };

  return (
    <div className={`bg-white rounded-2xl shadow-2xl overflow-hidden transition-all duration-300 ${isFullscreen ? 'fixed inset-0 z-50 rounded-none' : ''}`}>
      {/* PDF Player Header */}
      <div className="bg-gradient-to-r from-gray-800 to-gray-900 text-white p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <DocumentIcon className="h-6 w-6" />
            <span className="font-semibold">{(title || t('annualReportTitlePrefix')) + ' - ' + t('annualReportLivePreview')}</span>
          </div>
          <div className="flex items-center space-x-2">
            {/* Search Button */}
            <button
              onClick={() => setShowSearch(!showSearch)}
              className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
              title={t('searchInPdf')}
            >
              <MagnifyingGlassIcon className="h-4 w-4" />
            </button>

            {/* Share Button */}
            <button
              onClick={() => setShowShare(!showShare)}
              className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
              title={t('sharePdf')}
            >
              <ShareIcon className="h-4 w-4" />
            </button>

            {/* Fullscreen Button */}
            <button
              onClick={handleFullscreen}
              className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
              title={isFullscreen ? t('exitFullscreen') : t('enterFullscreen')}
            >
              {isFullscreen ? (
                <ArrowsPointingInIcon className="h-4 w-4" />
              ) : (
                <ArrowsPointingOutIcon className="h-4 w-4" />
              )}
            </button>

            <button className="p-2 hover:bg-gray-700 rounded-lg transition-colors">
              <MagnifyingGlassMinusIcon className="h-4 w-4" />
            </button>
            <span className="text-sm">100%</span>
            <button className="p-2 hover:bg-gray-700 rounded-lg transition-colors">
              <MagnifyingGlassPlusIcon className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Search Bar */}
        {showSearch && (
          <div className="mt-3 space-y-3">
            <div className="flex space-x-2">
              <input
                type="text"
                placeholder={t('searchInPdfPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 px-3 py-2 bg-gray-700 text-white rounded-lg text-sm focus:outline-none focus:bg-gray-600"
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
              <button
                onClick={handleSearch}
                className="px-3 py-2 bg-teal-600 hover:bg-teal-700 rounded-lg text-sm transition-colors"
              >
                {t('searchButton')}
              </button>
              <button
                onClick={() => {
                  setShowSearch(false);
                  setSearchTerm('');
                  setSearchResults(null);
                  // Clear any text selections when closing search
                  if (window.getSelection) {
                    window.getSelection().removeAllRanges();
                  }
                }}
                className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>

            {/* Search Results */}
            {searchResults && (
              <div className="bg-gray-700 rounded-lg p-3 max-h-40 overflow-y-auto">
                {searchResults.count > 0 ? (
                  <div>
                    <p className="text-green-400 text-sm mb-2">
                      {t('searchFoundPrefix')} {searchResults.count} {searchResults.count !== 1 ? t('searchResultsPlural') : t('searchResultsSingular')} {t('searchFor')} "{searchResults.term}"
                    </p>
                    <div className="space-y-2">
                      {searchResults.results.slice(0, 5).map((result, index) => (
                        <button
                          key={index}
                          onClick={() => handleSearchResultClick(result.page)}
                          className="w-full text-left bg-gray-600 hover:bg-gray-500 rounded p-2 transition-colors"
                        >
                          <p className="text-white text-sm">
                            <span className="text-teal-400 font-semibold">{t('pageLabel')} {result.page}:</span> {result.content.substring(0, 80)}...
                          </p>
                          <p className="text-gray-300 text-xs mt-1">
                            {result.matches} {result.matches !== 1 ? t('matchesPlural') : t('matchesSingular')} • {t('clickToView')}
                          </p>
                        </button>
                      ))}
                      {searchResults.results.length > 5 && (
                        <p className="text-gray-400 text-xs text-center">
                          ... {t('andPrefix')} {searchResults.results.length - 5} {t('moreResultsSuffix')}
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center">
                    <p className="text-red-400 text-sm">{t('noResultsFound')} "{searchResults.term}"</p>
                    <p className="text-gray-400 text-xs mt-1">{t('tryDifferentKeywords')}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Share Options */}
        {showShare && (
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              onClick={() => handleShare('telegram')}
              className="px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm transition-colors flex items-center space-x-1"
            >
              <span>📱</span>
              <span>Telegram</span>
            </button>
            <button
              onClick={() => handleShare('twitter')}
              className="px-3 py-2 bg-sky-500 hover:bg-sky-600 rounded-lg text-sm transition-colors flex items-center space-x-1"
            >
              <span>🐦</span>
              <span>Twitter</span>
            </button>
            <button
              onClick={() => handleShare('whatsapp')}
              className="px-3 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-sm transition-colors flex items-center space-x-1"
            >
              <span>💬</span>
              <span>WhatsApp</span>
            </button>
            <button
              onClick={() => handleShare('email')}
              className="px-3 py-2 bg-gray-600 hover:bg-gray-700 rounded-lg text-sm transition-colors flex items-center space-x-1"
            >
              <span>✉️</span>
              <span>Email</span>
            </button>
          </div>
        )}
      </div>

      {/* PDF Content Area - Fixed height, no scrolling */}
      <div className="relative bg-gray-100" style={{ height: isFullscreen ? 'calc(100vh - 80px)' : '400px', overflow: 'hidden' }}>
        {/* Page turning overlay */}
        <div className={`absolute inset-0 z-10 pointer-events-none transition-transform duration-300 ${isTurning ? 'animate-pulse' : ''}`}>
          {isTurning && (
            <div className={`absolute inset-0 bg-white transform transition-transform duration-300 ${
              currentPage > 1 ? 'animate-page-turn-left' : 'animate-page-turn-right'
            }`}>
              <div className="w-full h-full bg-gradient-to-r from-transparent via-white to-transparent opacity-80"></div>
            </div>
          )}
        </div>

        {/* PDF Display - Using object element for current report */}
        <object
          data={`${pdfUrl || '/AAAPD_Annual_Report (2024).pdf'}#page=${currentPage}&view=FitH&toolbar=0&navpanes=0&scrollbar=0`}
          type="application/pdf"
          className="w-full h-full"
          style={{ border: 'none' }}
        >
          {/* Fallback for browsers that don't support PDF objects */}
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
            <div className="text-center">
              <DocumentIcon className="h-24 w-24 mx-auto text-gray-400 mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">{title || t('annualReportFallbackTitle')}</h3>
              <p className="text-gray-500 mb-4">{t('annualReportInteractivePreview')}</p>
              <p className="text-sm text-gray-400">
                {t('annualReportUnsupportedMessage')}<br />
                <a href={pdfUrl || '/AAAPD_Annual_Report (2024).pdf'} className="text-teal-600 hover:text-teal-700 underline">
                  {t('annualReportViewDirectLink')}
                </a>
              </p>
            </div>
          </div>
        </object>
      </div>
    </div>
  );
};

// Previous Reports Download Card
const ReportDownloadCard = ({ year, title, description, fileSize, downloadUrl }) => {
  const { t } = useLanguageContext();
  return (
    <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <DocumentIcon className="h-8 w-8 text-teal-600" />
          <div>
            <h3 className="font-bold text-gray-800">{title}</h3>
            <p className="text-sm text-gray-500">{year} • {fileSize}</p>
          </div>
        </div>
        <ArrowDownTrayIcon className="h-6 w-6 text-gray-400" />
      </div>

      <p className="text-gray-600 text-sm mb-4 leading-relaxed">
        {description}
      </p>

      <a
        href={downloadUrl}
        target="_blank"
        rel="noopener noreferrer"
        download
        className="w-full px-4 py-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-semibold rounded-lg hover:from-teal-600 hover:to-cyan-600 transition-all duration-300 flex items-center justify-center space-x-2"
      >
        <ArrowDownTrayIcon className="h-4 w-4" />
        <span>{t('downloadPdf')}</span>
      </a>
    </div>
  );
};

const AnnualReportPage = () => {
  const { t } = useLanguageContext();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const API_BASE_URL = 'https://aaapd.org.et/gallery_backend/api';
  const FILE_BASE_URL = API_BASE_URL.replace(/\/$/, '').replace(/\/api$/, '');
  const [latestReport, setLatestReport] = useState(null); // {id, filename, year, description, created_at}
  const [loadingReport, setLoadingReport] = useState(false);
  const [reports, setReports] = useState([]);

  const handleDownloadReport = () => {
    const link = document.createElement('a');
    if (latestReport?.id) {
      link.href = `${API_BASE_URL}/download_annual_report.php?id=${encodeURIComponent(latestReport.id)}`;
      link.download = latestReport.filename || `AAAPD_Annual_Report_${latestReport.year || ''}.pdf`;
    } else {
      // Fallback to static asset if API unavailable
      link.href = '/AAAPD_Annual_Report (2024).pdf';
      link.download = 'AAAPD_Annual_Report (2024).pdf';
    }
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Fetch latest annual report metadata and list
  useEffect(() => {
    let ignore = false;
    const run = async () => {
      try {
        setLoadingReport(true);
        const res = await fetch(`${API_BASE_URL}/get_annual_reports.php`);
        const data = await res.json().catch(() => ({}));
        const list = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
        // pick most recent by created_at desc
        const latest = list
          .slice()
          .sort((a,b) => new Date(b.created_at || b.createdAt || 0) - new Date(a.created_at || a.createdAt || 0))[0];
        if (!ignore) {
          setLatestReport(latest || null);
          setReports(list || []);
        }
      } catch (e) {
        if (!ignore) {
          setLatestReport(null);
          setReports([]);
        }
      } finally {
        if (!ignore) setLoadingReport(false);
      }
    };
    run();
    return () => { ignore = true; };
  }, []);

  // Build previous reports from API list excluding the latest
  const previousReports = React.useMemo(() => {
    if (!reports || reports.length === 0) return [];
    const sorted = reports.slice().sort((a,b) => new Date(b.created_at || b.createdAt || 0) - new Date(a.created_at || a.createdAt || 0));
    const rest = sorted.slice(1);
    return rest.map(r => ({
      year: r.year || '',
      title: `${t('annualReportCardTitle')} ${r.year || ''}`,
      description: r.description || '',
      fileSize: '',
      downloadUrl: r.id ? `${API_BASE_URL}/download_annual_report.php?id=${encodeURIComponent(r.id)}` : undefined,
    }));
  }, [reports, API_BASE_URL, t]);

  const hasReport = Boolean(latestReport && (latestReport.filename || latestReport.id));
  const currentYear = hasReport ? (latestReport?.year || '') : '';

  // PDF URL for the live preview – only when a report exists
  const viewerPdfUrl = hasReport && latestReport?.filename
    ? `${FILE_BASE_URL}/uploads/annual_reports/${encodeURIComponent(latestReport.filename)}`
    : null;

  return (
    <div className={`bg-gray-50 ${isFullscreen ? 'hidden' : ''}`}>
      <HomeNav />

      <main>
        {/* Hero Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left Side - Image */}
              <div className="relative">
                <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src="/annualreportimage.png"
                    alt="AAAPD Community Meeting"
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Floating badge */}
                {hasReport && (
                  <div className="absolute -top-4 -right-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white px-4 py-2 rounded-full shadow-lg">
                    <span className="text-sm font-semibold">{(latestReport?.year || '') + ' ' + t('reportLabel')}</span>
                  </div>
                )}
              </div>

              {/* Right Side - Text Content */}
              <div className="space-y-8">
                <div>
                  <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                    {t('annualReportHeroTitle')}
                  </h1>
                  <div className="w-24 h-1 bg-gradient-to-r from-teal-500 to-cyan-500 mb-6"></div>
                </div>

                <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
                  {hasReport ? (
                    latestReport?.description ? (
                      latestReport.description.split(/\n\n|\r\n\r\n|\n|\r/g).filter(Boolean).map((para, idx) => (
                        <p key={idx}>{para}</p>
                      ))
                    ) : null
                  ) : (
                    <p className="text-gray-600">No annual report has been uploaded yet.</p>
                  )}
                </div>

                {hasReport && (
                  <div>
                    <button
                      onClick={handleDownloadReport}
                      className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-bold rounded-xl hover:from-teal-600 hover:to-cyan-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 space-x-3"
                    >
                      <ArrowDownTrayIcon className="h-6 w-6" />
                      <span>{`${t('annualReportDownloadPrefix')} ${latestReport?.year || ''} ${t('annualReportDownloadSuffix')}`}</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* PDF Viewer Section */}
        {hasReport ? (
          <section className="py-20 bg-gray-50">
            <div className="max-w-6xl mx-auto px-6">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  {t('annualReportLivePreview')}
                </h2>
                <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                  {t('annualReportLivePreviewDesc')}
                </p>
              </div>

              <div className="max-w-5xl mx-auto">
                <PDFViewer
                  pdfUrl={viewerPdfUrl}
                  title={`${t('annualReportTitlePrefix')} ${currentYear}`}
                />
              </div>
            </div>
          </section>
        ) : (
          <section className="py-20 bg-gray-50">
            <div className="max-w-6xl mx-auto px-6 text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {t('annualReportLivePreview')}
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                No annual report is available to preview.
              </p>
            </div>
          </section>
        )}

        {/* Previous Reports Download Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {t('annualReportDownloadsTitle')}
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                {t('annualReportDownloadsDesc')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {previousReports.length === 0 ? (
                <p className="text-gray-500">{t('annualReportNoPrevious')}</p>
              ) : (
                previousReports.map((report, index) => (
                  <ReportDownloadCard key={index} {...report} />
                ))
              )}
            </div>

          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AnnualReportPage;
